import { Controller } from 'react-hook-form';
import type { Control, FieldErrors, UseFormRegister, UseFormSetValue } from 'react-hook-form';
import { Trash2, ChevronDown } from 'lucide-react';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { AutocompleteSelect } from '../../../components/common/AutocompleteSelect';
import { formatCurrency, formatRupiah, parseRupiah } from '../../../utils/format';
import type { SalesOrderFormData } from '../../../types/salesOrder';
import type { InventoryItem } from '../../../types/inventory';
import type { WeightUnit } from '../../../hooks/useWeightUnit';
import { useLanguage } from '../../../features/language/useLanguage';
import type { GbLotOption } from '../useSalesOrderForm';
import { SoLotPicker } from './SoLotPicker';
import { ITEM_TYPE_OPTIONS, buildPickerLabel, type ItemTypeValue } from './salesOrderItemHelpers';

interface SalesOrderItemRowProps {
  index: number;
  watchedItems: SalesOrderFormData['items'];
  inventoryItems: InventoryItem[];
  control: Control<SalesOrderFormData>;
  register: UseFormRegister<SalesOrderFormData>;
  setValue: UseFormSetValue<SalesOrderFormData>;
  errors: FieldErrors<SalesOrderFormData>;
  areFieldsDisabled: boolean;
  currencySymbol: string;
  displayUnit: WeightUnit;
  ownReservedByItem: Record<string, number>;
  gbLotsByItem: Record<string, GbLotOption[]>;
  quantityLimitErrors: Record<number, string>;
  onRemove: (index: number) => void;
}

/** A single editable sales-order line: item type, product, description, lot, qty, rate, amount. */
export function SalesOrderItemRow({
  index,
  watchedItems,
  inventoryItems,
  control,
  register,
  setValue,
  errors,
  areFieldsDisabled,
  currencySymbol,
  displayUnit,
  ownReservedByItem,
  gbLotsByItem,
  quantityLimitErrors,
  onRemove,
}: SalesOrderItemRowProps) {
  const { t } = useLanguage();
  const item = watchedItems?.[index];
  const lineAmount = (item?.quantity || 0) * (item?.unitPrice || 0) || 0;
  const selectedItem = inventoryItems.find((i) => i.id === item?.inventoryItemId);
  const hasZeroOrEmptyUnitPrice = Boolean(item?.inventoryItemId && Number(item?.unitPrice || 0) <= 0);

  return (
    <div className="flex flex-wrap lg:flex-nowrap gap-2 items-start border p-2 rounded-md bg-gray-50">
      <div className="relative w-full lg:w-auto lg:flex-[2] min-w-[180px]">
        <label className="block text-xs font-medium text-gray-500">{t('salesOrder.items.productService')}</label>
        <Listbox
          value={(item?.itemType ?? 'finished_good') as ItemTypeValue}
          onChange={(next: ItemTypeValue) => {
            setValue(`items.${index}.itemType`, next);
            setValue(`items.${index}.inventoryItemId`, '');
            setValue(`items.${index}.roastType`, '');
            setValue(`items.${index}.lotId`, '');
            setValue(`items.${index}.lotNumber`, '');
            setValue(`items.${index}.processType`, '');
            setValue(`items.${index}.name`, '');
            setValue(`items.${index}.description`, '');
            setValue(`items.${index}.unitPrice`, 0);
          }}
          disabled={areFieldsDisabled}
        >
          <ListboxButton
            data-test-id={`so-item-type-${index}`}
            title={t('salesOrder.items.itemType')}
            className="absolute top-0 right-0 inline-flex items-center gap-1 rounded px-1.5 py-1 text-[10px] font-bold leading-none bg-primary-500 text-white shadow-sm transition-colors hover:bg-primary-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400/60 disabled:bg-gray-100 disabled:text-gray-500 disabled:border disabled:border-gray-300 disabled:cursor-not-allowed"
          >
            {ITEM_TYPE_OPTIONS.find((o) => o.value === (item?.itemType ?? 'finished_good'))?.short}
            <ChevronDown className="h-3 w-3" aria-hidden="true" />
          </ListboxButton>
          <ListboxOptions
            anchor="bottom end"
            className="z-50 mt-1 w-36 origin-top-right overflow-hidden rounded-md bg-white py-1 shadow-lg ring-1 ring-black/5 focus:outline-none"
          >
            {ITEM_TYPE_OPTIONS.map((opt) => (
              <ListboxOption
                key={opt.value}
                value={opt.value}
                data-test-id={`so-item-type-${index}-option-${opt.value}`}
                className={({ focus, selected }: { focus: boolean; selected: boolean }) =>
                  `flex cursor-pointer items-center justify-between gap-2 px-2.5 py-1.5 text-xs transition-colors ${
                    focus ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                  } ${selected ? 'font-semibold text-primary-700' : ''}`
                }
              >
                <span className="font-mono text-[10px] font-bold tracking-wide">{opt.short}</span>
                <span className="text-[11px] text-gray-500">{opt.label}</span>
              </ListboxOption>
            ))}
          </ListboxOptions>
        </Listbox>
        <div className="mt-2">
          <Controller
            control={control}
            name={`items.${index}.name`}
            render={({ field: { onChange, value } }) => (
              <AutocompleteSelect
                disabled={areFieldsDisabled}
                dataTestId={`so-item-product-select-${index}`}
                value={value}
                onChange={(text, option) => {
                  onChange(text);
                  if (option) {
                    setValue(`items.${index}.inventoryItemId`, option.id);
                    const selected = inventoryItems.find((i) => i.id === option.id);
                    if (selected) {
                      // RB synthetic options carry id "${groupId}:${roastType}";
                      // split to persist roastType so the backend can reconstruct
                      // the (item_id, roast_type) RB group.
                      if (selected.itemType === 'roasted_bulk') {
                        const colonIdx = option.id.indexOf(':');
                        const rt = colonIdx > 0 ? option.id.slice(colonIdx + 1) : '';
                        setValue(`items.${index}.roastType`, rt);
                      } else {
                        setValue(`items.${index}.roastType`, '');
                      }
                      // GB lines default to FIFO — `lotId` stays
                      // empty (the FIFO sentinel; backend picks the
                      // oldest lot at consumption). The rate still
                      // mirrors the FIFO lot's unit_price per spec
                      // (rate follows lot, not item). User must
                      // explicitly pick a specific lot to switch
                      // into manual mode.
                      if (selected.itemType === 'green_bean') {
                        const fifoLot = gbLotsByItem[selected.id]?.[0];
                        setValue(`items.${index}.lotId`, '');
                        setValue(`items.${index}.lotNumber`, '');
                        setValue(`items.${index}.processType`, '');
                        if (fifoLot) {
                          setValue(`items.${index}.unitPrice`, fifoLot.unitPrice);
                        } else {
                          setValue(`items.${index}.unitPrice`, selected.standardCost ?? 0);
                        }
                      } else {
                        setValue(`items.${index}.lotId`, '');
                        setValue(`items.${index}.lotNumber`, '');
                        setValue(`items.${index}.processType`, '');
                        const defaultRate =
                          selected.estimatedSellingPrice != null && !Number.isNaN(selected.estimatedSellingPrice)
                            ? selected.estimatedSellingPrice
                            : selected.standardCost ?? 0;
                        setValue(`items.${index}.unitPrice`, defaultRate);
                      }
                    }
                    if (selected?.description) {
                      setValue(`items.${index}.description`, selected.description);
                    }
                  } else {
                    setValue(`items.${index}.inventoryItemId`, '');
                    setValue(`items.${index}.roastType`, '');
                    setValue(`items.${index}.lotId`, '');
                    setValue(`items.${index}.lotNumber`, '');
                    setValue(`items.${index}.processType`, '');
                    setValue(`items.${index}.description`, '');
                  }
                }}
                options={inventoryItems
                  .filter(
                    (i) =>
                      i.itemType === item?.itemType &&
                      (i.id === item?.inventoryItemId ||
                        !watchedItems?.some((wi, wiIndex) => wiIndex !== index && wi.inventoryItemId === i.id))
                  )
                  // Hide FG options with no stock — an FG at 0 Pcs can't be sold.
                  // The currently selected line keeps its option so edits don't break.
                  .filter(
                    (i) =>
                      i.id === item?.inventoryItemId ||
                      i.itemType !== 'finished_good' ||
                      i.stock + (ownReservedByItem[i.id] || 0) > 0
                  )
                  .map((itemOption) => ({
                    id: itemOption.id,
                    label: buildPickerLabel(itemOption, displayUnit, itemOption.stock + (ownReservedByItem[itemOption.id] || 0)),
                    text: itemOption.name,
                  }))}
                placeholder={t('salesOrder.items.selectProductService')}
                className="text-xs"
                size="sm"
                allowCustom={false}
              />
            )}
          />
        </div>
        {errors.items?.[index]?.inventoryItemId && (
          <p className="text-xs text-red-600">{errors.items[index]?.inventoryItemId?.message}</p>
        )}
      </div>

      <div className="w-full lg:w-auto lg:flex-[3] min-w-[200px]">
        <label className="block text-xs font-medium text-gray-500">{t('inventory.modal.fields.description')}</label>
        <input
          data-test-id={`so-item-description-${index}`}
          disabled={true}
          type="text"
          {...register(`items.${index}.description`)}
          className="mt-2 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border bg-gray-100"
        />
      </div>

      {item?.itemType === 'green_bean' && selectedItem && (
        <SoLotPicker
          index={index}
          disabled={areFieldsDisabled}
          selectedLotId={item?.lotId || ''}
          lots={gbLotsByItem[selectedItem.id] || []}
          onSelect={(lot) => {
            if (!lot) {
              // FIFO sentinel — clear pin, recompute price from FIFO lot.
              const fifo = gbLotsByItem[selectedItem.id]?.[0];
              setValue(`items.${index}.lotId`, '');
              setValue(`items.${index}.lotNumber`, '');
              setValue(`items.${index}.processType`, '');
              if (fifo) setValue(`items.${index}.unitPrice`, fifo.unitPrice);
              return;
            }
            setValue(`items.${index}.lotId`, lot.id);
            setValue(`items.${index}.lotNumber`, lot.lotNumber);
            setValue(`items.${index}.processType`, lot.processType);
            setValue(`items.${index}.unitPrice`, lot.unitPrice);
          }}
        />
      )}

      {!areFieldsDisabled && (
        <div className="w-[48%] sm:w-[23%] lg:w-24">
          <label className="block text-xs font-medium text-gray-500">{t('salesOrder.items.remaining')}</label>
          <input
            disabled={true}
            value={
              !selectedItem
                ? ''
                : (() => {
                    const effective = selectedItem.stock + (ownReservedByItem[selectedItem.id] || 0);
                    if (selectedItem.itemType === 'finished_good') return effective;
                    return displayUnit === 'gram' ? Math.round(effective * 1000) : effective;
                  })()
            }
            className="mt-2 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border bg-gray-100"
          />
        </div>
      )}

      <div className="w-[48%] sm:w-[23%] lg:w-20">
        <label className="block text-xs font-medium text-gray-500">
          {item?.itemType === 'finished_good'
            ? t('reports.details.qty')
            : displayUnit === 'gram'
              ? t('salesOrder.items.qtyG')
              : t('salesOrder.items.qtyKg')}
        </label>
        {item?.itemType === 'finished_good' ? (
          <input
            disabled={areFieldsDisabled}
            data-test-id={`so-create-item-quantity-${index}`}
            type="number"
            step="1"
            min="1"
            inputMode="decimal"
            {...register(`items.${index}.quantity`, { valueAsNumber: true })}
            className="mt-2 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border"
          />
        ) : (
          <Controller
            control={control}
            name={`items.${index}.quantity`}
            render={({ field: { value, onChange } }) => (
              <input
                disabled={areFieldsDisabled}
                data-test-id={`so-create-item-quantity-${index}`}
                type="number"
                step={displayUnit === 'gram' ? '1' : '0.001'}
                min={displayUnit === 'gram' ? '1' : '0.001'}
                inputMode="decimal"
                value={
                  value === undefined || value === null || Number.isNaN(value)
                    ? ''
                    : displayUnit === 'gram'
                      ? Math.round((value as number) * 1000)
                      : value
                }
                onChange={(e) => {
                  const raw = parseFloat(e.target.value || '0');
                  if (Number.isNaN(raw)) {
                    onChange(0);
                    return;
                  }
                  onChange(displayUnit === 'gram' ? raw / 1000 : raw);
                }}
                className="mt-2 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border"
              />
            )}
          />
        )}
        {errors.items?.[index]?.quantity && (
          <p className="text-xs text-red-600 whitespace-nowrap">{errors.items[index]?.quantity?.message}</p>
        )}
        {!errors.items?.[index]?.quantity && quantityLimitErrors[index] && (
          <p className="text-xs text-red-600 whitespace-nowrap">*{quantityLimitErrors[index]}</p>
        )}
      </div>

      <div className="w-[48%] sm:w-[23%] lg:w-28">
        <label className="block text-xs font-medium text-gray-500">
          {item?.itemType === 'finished_good'
            ? t('salesOrder.items.rate', { currencySymbol })
            : t('salesOrder.items.ratePerKg', { currencySymbol })}
        </label>
        <Controller
          control={control}
          name={`items.${index}.unitPrice`}
          rules={{
            validate: (value) => Number(value) > 0 || t('salesOrder.items.unitPriceGreaterThanZero'),
          }}
          render={({ field: { value, onChange } }) => (
            <input
              type="text"
              value={formatRupiah(value)}
              onChange={(e) => onChange(parseRupiah(e.target.value))}
              disabled={areFieldsDisabled}
              data-test-id={`so-item-price-input-${index}`}
              className="mt-2 p-2.5 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-xs border"
            />
          )}
        />
        {errors.items?.[index]?.unitPrice && (
          <p className="text-xs text-red-600">{errors.items[index]?.unitPrice?.message}</p>
        )}
        {!errors.items?.[index]?.unitPrice && hasZeroOrEmptyUnitPrice && (
          <p className="text-xs text-red-600">{t('salesOrder.items.unitPriceGreaterThanZero')}</p>
        )}
      </div>

      <div className="w-[48%] sm:w-[23%] lg:w-28 text-right">
        <label className="block text-xs font-medium text-gray-500">{t('reports.general.table.amount')}</label>
        <div
          data-test-id={`so-item-amount-${index}`}
          className="mt-2 p-2.5 flex items-center justify-end rounded-md border border-gray-200 bg-white text-xs font-medium text-gray-700"
        >
          {formatCurrency(lineAmount, currencySymbol)}
        </div>
      </div>

      <div className="w-full sm:w-auto pt-2 lg:pt-5 flex justify-end">
        {!areFieldsDisabled && (
          <button
            type="button"
            data-test-id={`so-item-delete-${index}`}
            onClick={() => onRemove(index)}
            className="text-red-600 hover:text-red-800"
          >
            <Trash2 className="h-6 w-8" />
          </button>
        )}
      </div>
    </div>
  );
}
